import type { ReactNode } from 'react';

/**
 * A tiny form driven by a module's `settingsSchema` (ADR-0015). Handles the
 * property types a widget module is expected to use: string, number / integer,
 * boolean, and enum. Anything richer falls back to a JSON textarea.
 */

interface JsonSchema {
  type?: string;
  title?: string;
  description?: string;
  enum?: unknown[];
  default?: unknown;
  format?: string;
  minimum?: number;
  maximum?: number;
  properties?: Record<string, JsonSchema>;
  required?: string[];
}

export function SchemaForm({
  schema,
  value,
  onChange,
  stacked = false,
}: {
  schema: Record<string, unknown> | null | undefined;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** One full-width labelled field per line (side panel / mobile sheet) instead of a compact wrapping row. */
  stacked?: boolean;
}): ReactNode {
  const s = (schema ?? {}) as JsonSchema;
  const props = s.properties ?? {};
  const entries = Object.entries(props);

  if (entries.length === 0) {
    return (
      <label className={stacked ? 'field' : 'field small'} style={stacked ? undefined : { flex: 1 }}>
        Options (JSON)
        <textarea
          rows={3}
          value={JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value) as Record<string, unknown>);
            } catch {
              /* keep typing */
            }
          }}
        />
      </label>
    );
  }

  const set = (key: string, v: unknown): void => onChange({ ...value, [key]: v });
  const required = new Set(s.required ?? []);

  return (
    <div className={stacked ? 'stack schema-form' : 'row wrap small'} style={stacked ? undefined : { gap: 8, flex: 1 }}>
      {entries.map(([key, field]) => (
        <Field
          key={key}
          stacked={stacked}
          name={key}
          field={field}
          required={required.has(key)}
          value={value[key]}
          onChange={(v) => set(key, v)}
        />
      ))}
    </div>
  );
}

function Field({
  name,
  field,
  required,
  value,
  onChange,
  stacked,
}: {
  name: string;
  field: JsonSchema;
  required: boolean;
  value: unknown;
  onChange: (v: unknown) => void;
  stacked: boolean;
}): ReactNode {
  const label = (field.title ?? name) + (required ? ' *' : '');
  const fieldClass = stacked ? 'field' : 'field small';

  if (Array.isArray(field.enum)) {
    return (
      <label className={fieldClass}>
        {label}
        <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          {field.enum.map((opt) => (
            <option key={String(opt)} value={String(opt)}>
              {String(opt)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === 'boolean') {
    return (
      <label className={`${fieldClass} row`} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
    );
  }

  if (field.type === 'number' || field.type === 'integer') {
    return (
      <label className={fieldClass}>
        {label}
        <input
          type="number"
          value={value === undefined || value === null ? '' : Number(value)}
          min={field.minimum}
          max={field.maximum}
          onChange={(e) => {
            const n = e.target.value === '' ? undefined : Number(e.target.value);
            onChange(field.type === 'integer' && n !== undefined ? Math.round(n) : n);
          }}
        />
      </label>
    );
  }

  const isColor = field.format === 'color' || /colour|color/i.test(name);
  if (isColor && stacked) {
    const hex = String(value ?? '');
    return (
      <label className="field">
        {label}
        <span className="color-field">
          <input type="color" value={/^#[0-9a-f]{6}$/i.test(hex) ? hex : '#000000'} onChange={(e) => onChange(e.target.value)} />
          <input type="text" value={hex} placeholder={field.description ?? '#000000'} onChange={(e) => onChange(e.target.value)} />
        </span>
      </label>
    );
  }
  return (
    <label className={fieldClass}>
      {label}
      <input
        type={isColor ? 'color' : 'text'}
        value={String(value ?? '')}
        placeholder={field.description}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
